import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSiteId } from "../hooks/useSiteId";
import { useSiteStore } from "../stores/siteStore";
import { api, getFleetReport, getMovementRate, listWarehouses, putMovementRate } from "../api/rest";
import type { AssetType } from "../api/rest";
import { parseDecimal } from "../app/format";
import css from "./ShiftsAdminPage.module.css";

/**
 * ADR 288 (movement-cost slice 1): the money layer's editor — warehouse rates
 * and cycle constants (movement_rate) plus the per-type hourly rate
 * (asset_type.hour_cost), side by side because a report prices an asset by
 * type first, warehouse default second. Style-borrows the shifts admin page
 * (the OeePage-on-FleetPage precedent).
 *
 * The GTP benchmark presets are the commercial model's four reference rates
 * (Frota - Custo Hora tab, 2026-08): a starting point to overwrite with the
 * customer's real numbers, never an authority.
 */

export const GTP_PRESETS: { key: string; label: string; value: number }[] = [
  { key: "glp", label: "Contrabalançada GLP/Diesel", value: 76.37 },
  { key: "electric", label: "Contrabalançada Elétrica", value: 57.86 },
  { key: "reach", label: "Retrátil (Reach Truck)", value: 69.54 },
  { key: "pallet", label: "Transpaleteira Elétrica", value: 42.08 },
];

/** Text state for one numeric field; blank = null on the wire. */
function text(v: number | null | undefined): string {
  return v == null ? "" : String(v).replace(".", ",");
}

export function RatesAdminPage() {
  const { t } = useTranslation();
  const siteId = useSiteId();
  const qc = useQueryClient();
  const activeWarehouseId = useSiteStore((s) => s.activeWarehouseId);

  const { data: warehouses = [] } = useQuery({
    queryKey: ["warehouses", siteId],
    queryFn: () => listWarehouses(siteId),
  });

  const [pickedWarehouseId, setPickedWarehouseId] = useState("");
  const warehouseId =
    pickedWarehouseId && warehouses.some((w) => w.id === pickedWarehouseId)
      ? pickedWarehouseId
      : activeWarehouseId && warehouses.some((w) => w.id === activeWarehouseId)
        ? activeWarehouseId
        : warehouses[0]?.id ?? "";

  const { data: rate, isLoading } = useQuery({
    queryKey: ["movementRate", siteId, warehouseId],
    queryFn: () => getMovementRate(siteId, warehouseId),
    enabled: !!warehouseId,
  });
  const { data: types = [] } = useQuery({
    queryKey: ["assetTypes", siteId],
    queryFn: () => api.listAssetTypes(siteId),
  });

  // ADR 289: measured speeds over the trailing 30 d — a HINT applied by hand,
  // never silently: a simulation quote must be reproducible, so the simulator
  // reads only the configured values below. Window frozen at mount (the
  // SafetyPage idiom) so the query key is stable.
  const [nowMs] = useState(() => Date.now());
  const { data: measured } = useQuery({
    queryKey: ["fleet-report-hint", siteId, warehouseId],
    queryFn: () => getFleetReport(siteId, warehouseId, nowMs - 30 * 24 * 3_600_000, nowMs),
    enabled: !!siteId && !!warehouseId,
    staleTime: 300_000,
    retry: 0,
  });
  const measuredLoaded = measured?.fleet.avgLoadedSpeedMps ?? null;
  const measuredEmpty = measured?.fleet.avgEmptySpeedMps ?? null;

  // Editor state, seeded once per fetched identity (guarded adjust-during-render,
  // ADR 136 / the ShiftsAdminPage pattern).
  const [form, setForm] = useState({
    defaultHourCost: "", sqmMonthCost: "", currency: "BRL", pickS: "25", dropS: "25",
    liftSpeedMps: "0,35", loadedSpeedMps: "", emptySpeedMps: "",
  });
  const [typeCosts, setTypeCosts] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);
  const [seededFrom, setSeededFrom] = useState<unknown>(null);
  if (rate && rate !== seededFrom) {
    setSeededFrom(rate);
    setForm({
      defaultHourCost: text(rate.defaultHourCost),
      sqmMonthCost: text(rate.sqmMonthCost ?? null),
      currency: rate.currency,
      pickS: String(rate.pickS),
      dropS: String(rate.dropS),
      liftSpeedMps: text(rate.liftSpeedMps),
      loadedSpeedMps: text(rate.loadedSpeedMps),
      emptySpeedMps: text(rate.emptySpeedMps),
    });
    // `saved` deliberately survives this re-seed: the post-save invalidation
    // refetches the same values, and clearing here would blink the ✓ away.
    // User edits and warehouse switches clear it instead.
  }
  // Types arrive on their own query — their editor state seeds on their own
  // identity, or a late types fetch would be stuck with an empty snapshot.
  const [typesSeededFrom, setTypesSeededFrom] = useState<unknown>(null);
  if (types.length > 0 && types !== typesSeededFrom) {
    setTypesSeededFrom(types);
    setTypeCosts(Object.fromEntries(types.map((ty) => [ty.typeCode, text(ty.hourCost)])));
  }

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setSaved(false);
  };

  // Validation: money >= 0 or blank; ints >= 0; speeds > 0 or blank.
  const errors: string[] = [];
  const money = parseDecimal(form.defaultHourCost);
  if (money !== null && (!Number.isFinite(money) || money < 0)) errors.push(t("ratesAdmin.err.money"));
  const sqm = parseDecimal(form.sqmMonthCost);
  if (sqm !== null && (!Number.isFinite(sqm) || sqm < 0)) errors.push(t("ratesAdmin.err.sqmMoney"));
  for (const k of ["pickS", "dropS"] as const) {
    const v = parseDecimal(form[k]);
    if (v === null || !Number.isFinite(v) || !Number.isInteger(v) || v < 0)
      errors.push(t("ratesAdmin.err.int", { field: t(`ratesAdmin.${k}`) }));
  }
  const lift = parseDecimal(form.liftSpeedMps);
  if (lift === null || !Number.isFinite(lift) || lift <= 0)
    errors.push(t("ratesAdmin.err.speed", { field: t("ratesAdmin.liftSpeedMps") }));
  for (const k of ["loadedSpeedMps", "emptySpeedMps"] as const) {
    const v = parseDecimal(form[k]);
    if (v !== null && (!Number.isFinite(v) || v <= 0))
      errors.push(t("ratesAdmin.err.speed", { field: t(`ratesAdmin.${k}`) }));
  }
  for (const [code, s] of Object.entries(typeCosts)) {
    const v = parseDecimal(s);
    if (v !== null && (!Number.isFinite(v) || v < 0))
      errors.push(t("ratesAdmin.err.typeMoney", { type: code }));
  }

  const changedTypes = types.filter((ty) => (typeCosts[ty.typeCode] ?? text(ty.hourCost)) !== text(ty.hourCost));
  const rateDirty = rate != null && (
    form.defaultHourCost !== text(rate.defaultHourCost)
    || form.sqmMonthCost !== text(rate.sqmMonthCost ?? null)
    || form.currency !== rate.currency
    || form.pickS !== String(rate.pickS)
    || form.dropS !== String(rate.dropS)
    || form.liftSpeedMps !== text(rate.liftSpeedMps)
    || form.loadedSpeedMps !== text(rate.loadedSpeedMps)
    || form.emptySpeedMps !== text(rate.emptySpeedMps));
  const dirty = rateDirty || changedTypes.length > 0;

  const save = useMutation({
    mutationFn: async () => {
      if (rateDirty) {
        await putMovementRate(siteId, warehouseId, {
          defaultHourCost: parseDecimal(form.defaultHourCost),
          sqmMonthCost: parseDecimal(form.sqmMonthCost),
          currency: form.currency.trim() || "BRL",
          pickS: parseDecimal(form.pickS) ?? 25,
          dropS: parseDecimal(form.dropS) ?? 25,
          liftSpeedMps: parseDecimal(form.liftSpeedMps) ?? 0.35,
          loadedSpeedMps: parseDecimal(form.loadedSpeedMps),
          emptySpeedMps: parseDecimal(form.emptySpeedMps),
        });
      }
      for (const ty of changedTypes) {
        await api.updateAssetType(siteId, ty.typeCode, { hourCost: parseDecimal(typeCosts[ty.typeCode] ?? "") });
      }
    },
    onSuccess: () => {
      setSaved(true);
      setSeededFrom(null);   // re-seed from the refetched truth
      void qc.invalidateQueries({ queryKey: ["movementRate", siteId, warehouseId] });
      void qc.invalidateQueries({ queryKey: ["assetTypes", siteId] });
    },
  });

  const num = (k: keyof typeof form, labelKey: string, hintKey?: string) => (
    <div className={css.field}>
      <label className={css.label} htmlFor={`rate-${k}`}>{t(labelKey)}</label>
      <input id={`rate-${k}`} className={css.input} style={{ fontFamily: "var(--font-mono)" }}
             inputMode="decimal" value={form[k]} onChange={set(k)}
             placeholder={hintKey ? t(hintKey) : undefined} />
    </div>
  );

  return (
    <div className={css.root}>
      <header className={css.header}>
        <h1 className={css.title}>{t("ratesAdmin.title")}</h1>
        <p className={css.subtitle}>{t("ratesAdmin.subtitle")}</p>
      </header>

      <div className={css.selectorRow}>
        <div className={`${css.field} ${css.grow}`}>
          <label className={css.label} htmlFor="rates-warehouse">{t("ratesAdmin.warehouse")}</label>
          <select id="rates-warehouse" className={css.select} value={warehouseId}
                  disabled={warehouses.length === 0}
                  onChange={(e) => { setPickedWarehouseId(e.target.value); setSeededFrom(null); setSaved(false); }}>
            {warehouses.length === 0 && <option value="">{t("ratesAdmin.noWarehouses")}</option>}
            {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </div>
      </div>

      {warehouses.length === 0 ? (
        <div className={css.card}>{t("ratesAdmin.noWarehouses")}</div>
      ) : isLoading || !rate ? (
        <div className={css.card}><div className={`${css.skeleton} ${css.skelBar}`} /><div className={`${css.skeleton} ${css.skelRow}`} /></div>
      ) : (
        <>
          <section className={css.card}>
            <h2 className={css.cardTitle}>{t("ratesAdmin.warehouseCard")}</h2>
            <div className={css.selectorRow}>
              {num("defaultHourCost", "ratesAdmin.defaultHourCost", "ratesAdmin.noRateHint")}
              {num("sqmMonthCost", "ratesAdmin.sqmMonthCost", "ratesAdmin.noRateHint")}
              <div className={css.field}>
                <label className={css.label} htmlFor="rate-currency">{t("ratesAdmin.currency")}</label>
                <input id="rate-currency" className={css.input} style={{ width: 72, fontFamily: "var(--font-mono)" }}
                       value={form.currency}
                       onChange={(e) => { setForm((f) => ({ ...f, currency: e.target.value })); setSaved(false); }} />
              </div>
            </div>
            <p className={css.subtitle}>{t("ratesAdmin.noRateNote")}</p>
            <div className={css.selectorRow}>
              {num("pickS", "ratesAdmin.pickS")}
              {num("dropS", "ratesAdmin.dropS")}
              {num("liftSpeedMps", "ratesAdmin.liftSpeedMps")}
            </div>
            <div className={css.selectorRow}>
              {num("loadedSpeedMps", "ratesAdmin.loadedSpeedMps", "ratesAdmin.autoHint")}
              {num("emptySpeedMps", "ratesAdmin.emptySpeedMps", "ratesAdmin.autoHint")}
            </div>
            {(measuredLoaded != null || measuredEmpty != null) && (
              <p className={css.subtitle} role="status">
                {t("ratesAdmin.measured", {
                  loaded: measuredLoaded == null ? "—" : measuredLoaded.toFixed(2).replace(".", ","),
                  empty: measuredEmpty == null ? "—" : measuredEmpty.toFixed(2).replace(".", ","),
                })}{" "}
                <button type="button" className={css.btn} style={{ padding: "2px 8px", fontSize: 11 }}
                        onClick={() => {
                          setForm((f) => ({
                            ...f,
                            loadedSpeedMps: measuredLoaded == null ? f.loadedSpeedMps : String(measuredLoaded).replace(".", ","),
                            emptySpeedMps: measuredEmpty == null ? f.emptySpeedMps : String(measuredEmpty).replace(".", ","),
                          }));
                          setSaved(false);
                        }}>
                  {t("ratesAdmin.applyMeasured")}
                </button>
              </p>
            )}
            <p className={css.subtitle}>{t("ratesAdmin.autoNote")}</p>
          </section>

          <section className={css.card}>
            <h2 className={css.cardTitle}>{t("ratesAdmin.typesCard")}</h2>
            {types.length === 0 ? (
              <p className={css.subtitle}>{t("ratesAdmin.noTypes")}</p>
            ) : (
              types.map((ty: AssetType) => (
                <div key={ty.typeCode} className={css.selectorRow}>
                  <div className={`${css.field} ${css.grow}`}>
                    <span className={css.label}>{ty.label}</span>
                    <span style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>{ty.typeCode}</span>
                  </div>
                  <div className={css.field}>
                    <label className={css.label} htmlFor={`type-cost-${ty.typeCode}`}>
                      {t("ratesAdmin.typeHourCost", { currency: form.currency || "BRL" })}
                    </label>
                    <input id={`type-cost-${ty.typeCode}`} className={css.input}
                           style={{ fontFamily: "var(--font-mono)" }} inputMode="decimal"
                           value={typeCosts[ty.typeCode] ?? ""}
                           placeholder={t("ratesAdmin.noRateHint")}
                           onChange={(e) => { setTypeCosts((m) => ({ ...m, [ty.typeCode]: e.target.value })); setSaved(false); }} />
                  </div>
                  <div className={css.field}>
                    <label className={css.label} htmlFor={`type-preset-${ty.typeCode}`}>{t("ratesAdmin.preset")}</label>
                    <select id={`type-preset-${ty.typeCode}`} className={css.select} value=""
                            onChange={(e) => {
                              const p = GTP_PRESETS.find((x) => x.key === e.target.value);
                              if (p) { setTypeCosts((m) => ({ ...m, [ty.typeCode]: String(p.value).replace(".", ",") })); setSaved(false); }
                            }}>
                      <option value="">{t("ratesAdmin.presetPick")}</option>
                      {GTP_PRESETS.map((p) => (
                        <option key={p.key} value={p.key}>{p.label} — {String(p.value).replace(".", ",")}</option>
                      ))}
                    </select>
                  </div>
                </div>
              ))
            )}
            <p className={css.subtitle}>{t("ratesAdmin.presetNote")}</p>
          </section>

          <footer className={css.footer}>
            <button type="button" className={css.btn}
                    disabled={errors.length > 0 || save.isPending || !dirty}
                    onClick={() => save.mutate()}>
              {save.isPending ? t("common.saving") : t("common.save")}
            </button>
            {errors.length > 0 ? (
              <ul className={css.errors}>{errors.map((e, i) => <li key={i} className={css.errorText}>{e}</li>)}</ul>
            ) : save.isError ? (
              <span className={css.errorText}>{t("ratesAdmin.saveError", { msg: (save.error as Error).message })}</span>
            ) : saved && !dirty ? (
              <span className={css.savedText}>✓ {t("ratesAdmin.saved")}</span>
            ) : dirty ? (
              <span className={css.dirtyHint}>{t("ratesAdmin.unsaved")}</span>
            ) : null}
          </footer>
        </>
      )}
    </div>
  );
}
